// ---------------------------------------------- modules import
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FirebaseReducer,
  isEmpty,
  isLoaded,
  useFirestoreConnect
} from "react-redux-firebase";

import { AppState } from "../../../store/rootReducer";
import { IUserData, IOrderedUser } from "../../../store/firebase/common";
import PermissionHelper, {
  MenuIndex
} from "../../../store/helpers/permissionHelper";
import ValidationHelper, {
  pattern
} from "../../../store/helpers/validationHelper";
import { IUser, newUser } from "../../../store/user/common";
import { thunkLogout } from "../../../store/auth/thunkActions";
import {
  thunkUpdateUser,
  thunkUpdatePassword
} from "../../../store/user/thunkActions";

// ---------------------------------------------- reduce ordered data
const reduceUsers = (orderedUsers: IOrderedUser[]) =>
  orderedUsers.reduce(
    (currentUsers, orderedUser) => [
      ...currentUsers,
      { ...orderedUser, password: "" }
    ],
    [] as IUser[]
  );

// ---------------------------------------------- custom hooks
export const useAuth = () => {
  // ---------------------------------------------- redux connect
  const auth = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).auth
  );
  const profile = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).profile
  );

  // ---------------------------------------------- local state
  const [areLoaded, setAreLoaded] = useState(
    isLoaded(auth) && isLoaded(profile)
  );
  const [isEmptied, setIsEmptied] = useState(isEmpty(auth) || isEmpty(profile));

  // ---------------------------------------------- effects
  useEffect(() => {
    setAreLoaded(isLoaded(auth) && isLoaded(profile));
    setIsEmptied(isEmpty(auth) || isEmpty(profile));
  }, [auth, profile]);

  return {
    auth,
    profile,
    areLoaded,
    isEmptied
  };
};

export const useUser = (index: MenuIndex, id?: string) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const dispatch = useDispatch();

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({
    collection: collectionPath,
    where: ["isSuper", "==", false]
  });

  // ---------------------------------------------- redux connect
  const orderedUsers = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedUser[]
  );

  // ---------------------------------------------- local state
  const [user, setUser] = useState<IUser>(newUser());
  const [newPassword, setNewPassword] = useState({
    password: "",
    confirmPassword: ""
  });

  const isNewPasswordValid = () =>
    ValidationHelper.validate(newPassword.password, pattern.login.password) &&
    ValidationHelper.validate(
      newPassword.confirmPassword,
      RegExp(`^${newPassword.password}$`)
    );

  // ---------------------------------------------- handlers
  const handleChange = (prop: string, value: string) =>
    setUser({
      ...user,
      [prop]: value
    });

  const handleChangePassword = (prop: string, value: string) =>
    setNewPassword({
      ...newPassword,
      [prop]: value
    });

  const handleUpdate = (profile: FirebaseReducer.Profile<IUserData>) => {
    if (profile) dispatch(thunkUpdateUser(user, collectionPath));
  };

  const handleUpdatePassword = (
    profile: FirebaseReducer.Profile<IUserData>
  ) => {
    if (profile) dispatch(thunkUpdatePassword(user, newPassword.password));

    dispatch(thunkLogout());
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedUsers) && !isEmpty(orderedUsers)) {
      const currentUsers = reduceUsers(orderedUsers);

      const currentUser = currentUsers.find(
        currentUser => currentUser.id === id
      );

      if (currentUser) setUser(currentUser);
    }
  }, [orderedUsers, id]);

  // ---------------------------------------------- return value
  return {
    user,
    isNewPasswordValid,
    onChange: handleChange,
    onChangePassword: handleChangePassword,
    onUpdate: handleUpdate,
    onUpdatePassword: handleUpdatePassword
  };
};

export const useToggle = () => {
  // ---------------------------------------------- local state
  const [isThemeReady, setIsThemeReady] = useState(false);
  const [isResetReady, setIsResetReady] = useState(false);

  // ---------------------------------------------- handlers
  const handleToggleTheme = () => {
    setIsResetReady(false);

    setIsThemeReady(!isThemeReady);
  };

  const handleToggleReset = () => {
    setIsThemeReady(false);

    setIsResetReady(!isResetReady);
  };

  // ---------------------------------------------- return value
  return {
    isThemeReady,
    isResetReady,
    onToggleTheme: handleToggleTheme,
    onToggleReset: handleToggleReset
  };
};
